import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Lightning, Plus, ArrowsClockwise, FileText, Calendar, 
  Buildings, Warning, CheckCircle, Clock, Trash, Eye,
  Target, ChartBar, CaretRight, UploadSimple, X, Spinner,
  Database, Users, Gavel, Shield, Brain, Gear, BookOpen,
  ArrowRight, Play, Pause, ClipboardText, Tag, Flag,
  CaretDown, CaretUp, PencilSimple, Check
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Helper to extract error message from Pydantic/FastAPI errors
const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

// Impact area configuration
const IMPACT_AREAS = {
  data: { label: 'Data & Systems', icon: Database, color: '#3B82F6' },
  systems: { label: 'IT Systems', icon: Gear, color: '#06B6D4' },
  processes: { label: 'Processes', icon: ArrowRight, color: '#10B981' },
  controls: { label: 'Controls', icon: Shield, color: '#F59E0B' },
  policies: { label: 'Policies', icon: BookOpen, color: '#8B5CF6' },
  roles: { label: 'Roles & Responsibilities', icon: Users, color: '#EC4899' },
  training: { label: 'Training', icon: Brain, color: '#14B8A6' },
  vendors: { label: 'Vendors/Third Parties', icon: Buildings, color: '#6366F1' },
};

const SEVERITY_CONFIG = {
  critical: { label: 'Critical', color: '#EF4444', bgColor: '#EF444415' },
  high: { label: 'High', color: '#F59E0B', bgColor: '#F59E0B15' },
  medium: { label: 'Medium', color: '#3B82F6', bgColor: '#3B82F615' },
  low: { label: 'Low', color: '#10B981', bgColor: '#10B98115' },
  none: { label: 'No Impact', color: '#6B7280', bgColor: '#6B728015' },
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: '#6B7280', icon: FileText },
  analysis: { label: 'Analysis', color: '#3B82F6', icon: Brain },
  impact_assessment: { label: 'Impact Assessment', color: '#F59E0B', icon: Target },
  action_planning: { label: 'Action Planning', color: '#8B5CF6', icon: ClipboardText },
  implementation: { label: 'Implementation', color: '#14B8A6', icon: Play },
  monitoring: { label: 'Monitoring', color: '#06B6D4', icon: ChartBar },
  completed: { label: 'Completed', color: '#10B981', icon: CheckCircle },
  archived: { label: 'Archived', color: '#9CA3AF', icon: Pause },
};

const ChangeManagementWorkspace = () => {
  const { user, token } = useAuth();
  const [initiatives, setInitiatives] = useState([]);
  const [stats, setStats] = useState(null);
  const [referenceData, setReferenceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [activeView, setActiveView] = useState('list'); // list | detail

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [initiativesRes, statsRes, refDataRes] = await Promise.all([
        axios.get(`${API}/change-management/initiatives`, { headers }),
        axios.get(`${API}/change-management/initiatives/stats`, { headers }),
        axios.get(`${API}/change-management/reference-data`, { headers })
      ]);
      setInitiatives(initiativesRes.data.initiatives || []);
      setStats(statsRes.data);
      setReferenceData(refDataRes.data);
    } catch (error) {
      console.error('Failed to load change management data:', error);
      toast.error('Failed to load change management data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInitiative = async (data) => {
    try {
      const res = await axios.post(`${API}/change-management/initiatives`, data, { headers });
      toast.success('Change initiative created');
      setShowCreateModal(false);
      loadData();
      // Open the newly created initiative
      setSelectedInitiative(res.data.initiative_id);
      setActiveView('detail');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create initiative'));
    }
  };

  const handleDeleteInitiative = async (id) => {
    if (!window.confirm('Are you sure you want to delete this change initiative? All associated data will be removed.')) {
      return;
    }
    try {
      await axios.delete(`${API}/change-management/initiatives/${id}`, { headers });
      toast.success('Initiative deleted');
      if (selectedInitiative === id) {
        setSelectedInitiative(null);
        setActiveView('list');
      }
      loadData();
    } catch (error) {
      toast.error('Failed to delete initiative');
    }
  };

  const openInitiativeDetail = (id) => {
    setSelectedInitiative(id);
    setActiveView('detail');
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <div className="ml-64 flex-1 overflow-y-auto">
        {/* Breadcrumb */}
        <div className="px-6 pt-4 bg-gradient-to-r from-amber-500/10 via-transparent to-transparent">
          <PageBreadcrumb items={[
            { label: 'Change Management' }
          ]} />
        </div>

        {/* Header */}
        <div className="px-6 pb-6 border-b border-border bg-gradient-to-r from-amber-500/10 via-transparent to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center border border-amber-500/30">
                <Lightning size={30} className="text-amber-400" weight="duotone" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-semibold text-text-primary">
                  Change Management Workspace
                </h1>
                <p className="text-sm text-text-secondary mt-1">
                  Manage regulatory changes, assess business impacts, and track implementation
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {activeView === 'detail' && selectedInitiative && (
                <Button
                  variant="outline"
                  onClick={() => { setActiveView('list'); setSelectedInitiative(null); }}
                  className="flex items-center gap-2"
                  data-testid="back-to-list-btn"
                >
                  <CaretRight size={16} className="rotate-180" />
                  Back to List
                </Button>
              )}
              <Button
                onClick={loadData}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="refresh-btn"
              >
                <ArrowsClockwise size={16} />
                Refresh
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="create-initiative-btn"
              >
                <Plus size={16} weight="bold" />
                New Change Initiative
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Dashboard */}
        {stats && activeView === 'list' && (
          <div className="p-6 border-b border-border bg-surface/50">
            <div className="grid grid-cols-5 gap-4">
              <StatCard 
                label="Total Initiatives" 
                value={stats.total} 
                color="#3B82F6"
                icon={FileText}
              />
              <StatCard 
                label="Active" 
                value={stats.active} 
                color="#10B981"
                icon={Play}
              />
              <StatCard 
                label="Overdue" 
                value={stats.overdue} 
                color="#EF4444"
                icon={Warning}
              />
              <StatCard 
                label="Actions Pending" 
                value={stats.actions?.pending || 0} 
                color="#F59E0B"
                icon={ClipboardText}
              />
              <StatCard 
                label="Actions Completed" 
                value={stats.actions?.completed || 0} 
                color="#14B8A6"
                icon={CheckCircle}
              />
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size={32} className="animate-spin text-amber-400" />
            </div>
          ) : activeView === 'detail' && selectedInitiative ? (
            <InitiativeDetailView
              initiativeId={selectedInitiative}
              token={token}
              onUpdate={loadData}
              onDelete={() => { handleDeleteInitiative(selectedInitiative); }}
              referenceData={referenceData}
            />
          ) : (
            <InitiativeListView
              initiatives={initiatives}
              onOpenDetail={openInitiativeDetail}
              onDelete={handleDeleteInitiative}
            />
          )}
        </div>
      </div>

      {/* Create Initiative Modal */}
      <CreateInitiativeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateInitiative}
        referenceData={referenceData}
      />
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, color, icon: Icon }) => (
  <div className="bg-background border border-border rounded-xl p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-text-tertiary uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-mono font-bold mt-1" style={{ color }}>{value}</p>
      </div>
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon size={20} style={{ color }} weight="duotone" />
      </div>
    </div>
  </div>
);

// Initiative List View
const InitiativeListView = ({ initiatives, onOpenDetail, onDelete }) => {
  if (initiatives.length === 0) {
    return (
      <div className="text-center py-20 bg-surface border border-border rounded-xl">
        <Lightning size={48} className="mx-auto text-amber-400 mb-4 opacity-50" />
        <h3 className="font-semibold text-text-primary mb-2">No Change Initiatives Yet</h3>
        <p className="text-sm text-text-secondary mb-4">
          Create your first change initiative to start tracking regulatory changes
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-text-primary">
        Change Initiatives ({initiatives.length})
      </h3>
      <div className="grid gap-4">
        {initiatives.map((initiative) => (
          <InitiativeCard
            key={initiative.id}
            initiative={initiative}
            onOpen={() => onOpenDetail(initiative.id)}
            onDelete={() => onDelete(initiative.id)}
          />
        ))}
      </div>
    </div>
  );
};

// Initiative Card Component
const InitiativeCard = ({ initiative, onOpen, onDelete }) => {
  const status = STATUS_CONFIG[initiative.status] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-surface border border-border rounded-xl p-5 hover:border-amber-500/30 transition-all cursor-pointer group"
      onClick={onOpen}
      data-testid={`initiative-card-${initiative.id}`}
    >
      <div className="flex items-start gap-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${status.color}15` }}
        >
          <StatusIcon size={24} style={{ color: status.color }} weight="duotone" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="font-semibold text-text-primary group-hover:text-amber-400 transition-colors">
                {initiative.title}
              </h4>
              <p className="text-sm text-text-secondary mt-1 line-clamp-2">
                {initiative.description || 'No description provided'}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <span 
                className="px-3 py-1 text-xs font-medium rounded-full"
                style={{ backgroundColor: `${status.color}15`, color: status.color }}
              >
                {status.label}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-4 mt-4 text-xs text-text-tertiary">
            {initiative.source_regulator && (
              <div className="flex items-center gap-1">
                <Buildings size={14} />
                <span>{initiative.source_regulator}</span>
              </div>
            )}
            {initiative.compliance_deadline && (
              <div className="flex items-center gap-1">
                <Calendar size={14} />
                <span>Due: {new Date(initiative.compliance_deadline).toLocaleDateString()}</span>
              </div>
            )}
            {initiative.impact_areas_count > 0 && (
              <div className="flex items-center gap-1">
                <Target size={14} />
                <span>{initiative.impact_areas_count} Impact Areas</span>
              </div>
            )}
            {initiative.total_actions > 0 && (
              <div className="flex items-center gap-1">
                <ClipboardText size={14} />
                <span>{initiative.completed_actions || 0}/{initiative.total_actions} Actions</span>
              </div>
            )}
          </div>
          
          {/* Progress Bar */}
          {initiative.total_actions > 0 && (
            <div className="mt-3">
              <div className="w-full h-1.5 bg-background rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-500 rounded-full transition-all"
                  style={{ width: `${initiative.progress_percentage || 0}%` }}
                />
              </div>
            </div>
          )}
        </div>
        
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-2 hover:bg-background rounded-lg transition-all text-text-tertiary hover:text-red-400 opacity-0 group-hover:opacity-100"
          data-testid={`delete-initiative-${initiative.id}`}
        >
          <Trash size={18} />
        </button>
      </div>
    </motion.div>
  );
};

// Initiative Detail View
const InitiativeDetailView = ({ initiativeId, token, onUpdate, onDelete, referenceData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview | impacts | actions
  const [uploading, setUploading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadInitiativeDetail();
  }, [initiativeId]);

  const loadInitiativeDetail = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/change-management/initiatives/${initiativeId}`, { headers });
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load initiative details');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/change-management/initiatives/${initiativeId}/upload-document`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Document uploaded. NAVI analysis started.');
      loadInitiativeDetail();
      onUpdate();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload document'));
    } finally {
      setUploading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(
        `${API}/change-management/initiatives/${initiativeId}/status`,
        { status: newStatus },
        { headers }
      );
      toast.success('Status updated');
      loadInitiativeDetail();
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleGenerateActions = async () => {
    try {
      const res = await axios.post(
        `${API}/change-management/initiatives/${initiativeId}/generate-actions`,
        {},
        { headers }
      );
      toast.success(`Generated ${res.data.created_count} action items`);
      loadInitiativeDetail();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate actions'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={32} className="animate-spin text-amber-400" />
      </div>
    );
  }

  if (!data || !data.initiative) {
    return (
      <div className="text-center py-20">
        <Warning size={48} className="mx-auto text-red-400 mb-4" />
        <h3 className="font-semibold text-text-primary">Initiative Not Found</h3>
      </div>
    );
  }

  const { initiative, impact_assessment, actions } = data;
  const status = STATUS_CONFIG[initiative.status] || STATUS_CONFIG.draft;

  return (
    <div className="space-y-6">
      {/* Initiative Header */}
      <div className="bg-surface border border-border rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${status.color}15` }}
            >
              <status.icon size={28} style={{ color: status.color }} weight="duotone" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary">
                {initiative.title}
              </h2>
              <p className="text-sm text-text-secondary mt-1 max-w-2xl">
                {initiative.description || 'No description'}
              </p>
              <div className="flex items-center gap-4 mt-3 text-xs text-text-tertiary">
                {initiative.source_regulator && (
                  <span className="flex items-center gap-1">
                    <Buildings size={14} /> {initiative.source_regulator}
                  </span>
                )}
                {initiative.compliance_deadline && (
                  <span className="flex items-center gap-1">
                    <Calendar size={14} /> Due: {new Date(initiative.compliance_deadline).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={initiative.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <SelectItem key={value} value={value}>
                    <span className="flex items-center gap-2">
                      <config.icon size={14} style={{ color: config.color }} />
                      {config.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline" className="text-red-400 hover:text-red-300" onClick={onDelete}>
              <Trash size={16} />
            </Button>
          </div>
        </div>
        
        {/* Document Upload Section */}
        {!initiative.source_document_path && (
          <div className="mt-6 p-4 border-2 border-dashed border-amber-500/30 rounded-xl bg-amber-500/5">
            <div className="text-center">
              <UploadSimple size={32} className="mx-auto text-amber-400 mb-2" />
              <p className="text-sm text-text-primary mb-2">Upload Regulatory Document for Analysis</p>
              <p className="text-xs text-text-tertiary mb-4">
                Upload a draft law, circular, or regulation for NAVI to analyze and extract key changes
              </p>
              <label className="inline-block">
                <input
                  type="file"
                  accept=".pdf,.docx,.doc"
                  onChange={(e) => handleFileUpload(e.target.files?.[0])}
                  className="hidden"
                  disabled={uploading}
                />
                <Button variant="outline" className="cursor-pointer" disabled={uploading}>
                  {uploading ? (
                    <><Spinner size={16} className="animate-spin mr-2" /> Uploading...</>
                  ) : (
                    <><UploadSimple size={16} className="mr-2" /> Select Document</>
                  )}
                </Button>
              </label>
            </div>
          </div>
        )}
        
        {initiative.source_document_path && (
          <div className="mt-6 p-4 bg-background rounded-xl border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText size={24} className="text-amber-400" />
                <div>
                  <p className="text-sm font-medium text-text-primary">{initiative.source_document_name}</p>
                  <p className="text-xs text-text-tertiary">
                    NAVI Analysis: {initiative.navi_analysis_status || 'pending'}
                  </p>
                </div>
              </div>
              {initiative.navi_analysis_status === 'completed' && (
                <CheckCircle size={20} className="text-emerald-400" weight="fill" />
              )}
              {initiative.navi_analysis_status === 'analyzing' && (
                <Spinner size={20} className="text-amber-400 animate-spin" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: Eye },
          { id: 'impacts', label: 'Impact Assessment', icon: Target, count: impact_assessment?.impacts?.length },
          { id: 'actions', label: 'Action Items', icon: ClipboardText, count: actions?.length },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-all ${
              activeTab === tab.id
                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30'
                : 'text-text-secondary hover:bg-surface'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                activeTab === tab.id ? 'bg-amber-500/20' : 'bg-background'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-surface border border-border rounded-xl p-6">
        {activeTab === 'overview' && (
          <OverviewTab initiative={initiative} />
        )}
        
        {activeTab === 'impacts' && (
          <ImpactsTab
            initiativeId={initiativeId}
            assessment={impact_assessment}
            token={token}
            onUpdate={loadInitiativeDetail}
            referenceData={referenceData}
          />
        )}
        
        {activeTab === 'actions' && (
          <ActionsTab
            initiativeId={initiativeId}
            actions={actions || []}
            token={token}
            onUpdate={loadInitiativeDetail}
            onGenerateActions={handleGenerateActions}
            referenceData={referenceData}
          />
        )}
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ initiative }) => {
  return (
    <div className="space-y-6">
      {/* Key Changes (from NAVI) */}
      {initiative.navi_key_changes && initiative.navi_key_changes.length > 0 && (
        <div>
          <h4 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
            <Brain size={18} className="text-cyan-400" />
            Key Changes Identified by NAVI
          </h4>
          <div className="space-y-3">
            {initiative.navi_key_changes.map((change, idx) => (
              <div key={idx} className="p-4 bg-background rounded-lg border border-border">
                <h5 className="font-medium text-text-primary">{change.title}</h5>
                <p className="text-sm text-text-secondary mt-1">{change.description}</p>
                {change.section_reference && (
                  <p className="text-xs text-text-tertiary mt-2">
                    Reference: {change.section_reference}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Executive Summary */}
      {initiative.navi_extracted_summary && (
        <div>
          <h4 className="font-semibold text-text-primary mb-3">Executive Summary</h4>
          <p className="text-sm text-text-secondary bg-background p-4 rounded-lg border border-border">
            {initiative.navi_extracted_summary}
          </p>
        </div>
      )}

      {/* Affected Areas */}
      {initiative.navi_affected_areas && initiative.navi_affected_areas.length > 0 && (
        <div>
          <h4 className="font-semibold text-text-primary mb-3">Affected Business Areas</h4>
          <div className="flex flex-wrap gap-2">
            {initiative.navi_affected_areas.map((area) => {
              const config = IMPACT_AREAS[area];
              if (!config) return null;
              return (
                <span
                  key={area}
                  className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-2"
                  style={{ backgroundColor: `${config.color}15`, color: config.color }}
                >
                  <config.icon size={14} />
                  {config.label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div>
        <h4 className="font-semibold text-text-primary mb-3">Timeline</h4>
        <div className="grid grid-cols-3 gap-4">
          {initiative.effective_date && (
            <div className="p-4 bg-background rounded-lg border border-border">
              <p className="text-xs text-text-tertiary">Effective Date</p>
              <p className="font-medium text-text-primary mt-1">
                {new Date(initiative.effective_date).toLocaleDateString()}
              </p>
            </div>
          )}
          {initiative.compliance_deadline && (
            <div className="p-4 bg-background rounded-lg border border-border">
              <p className="text-xs text-text-tertiary">Compliance Deadline</p>
              <p className="font-medium text-text-primary mt-1">
                {new Date(initiative.compliance_deadline).toLocaleDateString()}
              </p>
            </div>
          )}
          {initiative.internal_deadline && (
            <div className="p-4 bg-background rounded-lg border border-border">
              <p className="text-xs text-text-tertiary">Internal Deadline</p>
              <p className="font-medium text-text-primary mt-1">
                {new Date(initiative.internal_deadline).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Impacts Tab
const ImpactsTab = ({ initiativeId, assessment, token, onUpdate, referenceData }) => {
  const [showAddImpact, setShowAddImpact] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const handleAddImpact = async (impactData) => {
    try {
      await axios.post(
        `${API}/change-management/initiatives/${initiativeId}/impact/items`,
        impactData,
        { headers }
      );
      toast.success('Impact item added');
      setShowAddImpact(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to add impact item');
    }
  };

  const handleDeleteImpact = async (itemId) => {
    try {
      await axios.delete(
        `${API}/change-management/initiatives/${initiativeId}/impact/items/${itemId}`,
        { headers }
      );
      toast.success('Impact item removed');
      onUpdate();
    } catch (error) {
      toast.error('Failed to remove impact item');
    }
  };

  const impacts = assessment?.impacts || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-text-primary">
          Impact Assessment ({impacts.length} areas)
        </h4>
        <Button
          variant="outline"
          onClick={() => setShowAddImpact(true)}
          className="flex items-center gap-2"
        >
          <Plus size={16} />
          Add Impact
        </Button>
      </div>

      {impacts.length === 0 ? (
        <div className="text-center py-12 bg-background rounded-lg border border-border">
          <Target size={40} className="mx-auto text-text-tertiary mb-3" />
          <p className="text-text-secondary">No impacts assessed yet</p>
          <p className="text-xs text-text-tertiary mt-1">
            Upload a document for NAVI analysis or manually add impact items
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {impacts.map((impact) => {
            const areaConfig = IMPACT_AREAS[impact.area] || { label: impact.area, color: '#6B7280', icon: Target };
            const sevConfig = SEVERITY_CONFIG[impact.severity] || SEVERITY_CONFIG.medium;
            const AreaIcon = areaConfig.icon;
            
            return (
              <div
                key={impact.id}
                className="p-4 bg-background rounded-lg border border-border hover:border-amber-500/20 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${areaConfig.color}15` }}
                  >
                    <AreaIcon size={20} style={{ color: areaConfig.color }} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-text-primary">{areaConfig.label}</span>
                      <span 
                        className="px-2 py-0.5 text-xs rounded-full"
                        style={{ backgroundColor: sevConfig.bgColor, color: sevConfig.color }}
                      >
                        {sevConfig.label}
                      </span>
                      {impact.navi_extracted && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-cyan-500/10 text-cyan-400">
                          NAVI
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-secondary">{impact.description}</p>
                    
                    {(impact.current_state || impact.required_state) && (
                      <div className="grid grid-cols-2 gap-4 mt-3 text-xs">
                        {impact.current_state && (
                          <div>
                            <span className="text-text-tertiary">Current State:</span>
                            <p className="text-text-secondary mt-0.5">{impact.current_state}</p>
                          </div>
                        )}
                        {impact.required_state && (
                          <div>
                            <span className="text-text-tertiary">Required State:</span>
                            <p className="text-text-secondary mt-0.5">{impact.required_state}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => handleDeleteImpact(impact.id)}
                    className="p-2 hover:bg-surface rounded-lg transition-all text-text-tertiary hover:text-red-400"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Impact Modal */}
      <AddImpactModal
        isOpen={showAddImpact}
        onClose={() => setShowAddImpact(false)}
        onAdd={handleAddImpact}
        referenceData={referenceData}
      />
    </div>
  );
};

// Actions Tab
const ActionsTab = ({ initiativeId, actions, token, onUpdate, onGenerateActions, referenceData }) => {
  const [showAddAction, setShowAddAction] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const handleAddAction = async (actionData) => {
    try {
      await axios.post(
        `${API}/change-management/initiatives/${initiativeId}/actions`,
        actionData,
        { headers }
      );
      toast.success('Action item created');
      setShowAddAction(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to create action item');
    }
  };

  const handleUpdateAction = async (actionId, updates) => {
    try {
      await axios.put(
        `${API}/change-management/initiatives/${initiativeId}/actions/${actionId}`,
        updates,
        { headers }
      );
      toast.success('Action updated');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update action');
    }
  };

  const handleDeleteAction = async (actionId) => {
    try {
      await axios.delete(
        `${API}/change-management/initiatives/${initiativeId}/actions/${actionId}`,
        { headers }
      );
      toast.success('Action deleted');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete action');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-text-primary">
          Action Items ({actions.length})
        </h4>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={onGenerateActions}
            className="flex items-center gap-2 text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10"
          >
            <Brain size={16} />
            Generate from Impacts
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowAddAction(true)}
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            Add Action
          </Button>
        </div>
      </div>

      {actions.length === 0 ? (
        <div className="text-center py-12 bg-background rounded-lg border border-border">
          <ClipboardText size={40} className="mx-auto text-text-tertiary mb-3" />
          <p className="text-text-secondary">No action items yet</p>
          <p className="text-xs text-text-tertiary mt-1">
            Generate actions from impact assessment or add them manually
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {actions.map((action) => (
            <ActionItemCard
              key={action.id}
              action={action}
              onUpdate={(updates) => handleUpdateAction(action.id, updates)}
              onDelete={() => handleDeleteAction(action.id)}
            />
          ))}
        </div>
      )}

      {/* Add Action Modal */}
      <AddActionModal
        isOpen={showAddAction}
        onClose={() => setShowAddAction(false)}
        onAdd={handleAddAction}
        referenceData={referenceData}
      />
    </div>
  );
};

// Action Item Card
const ActionItemCard = ({ action, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  
  const statusColors = {
    not_started: '#6B7280',
    in_progress: '#3B82F6',
    blocked: '#EF4444',
    completed: '#10B981',
    cancelled: '#9CA3AF',
  };
  
  const priorityColors = {
    critical: '#EF4444',
    high: '#F59E0B',
    medium: '#3B82F6',
    low: '#10B981',
  };

  return (
    <div className="p-4 bg-background rounded-lg border border-border">
      <div className="flex items-start gap-3">
        <button
          onClick={() => onUpdate({ status: action.status === 'completed' ? 'not_started' : 'completed' })}
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
            action.status === 'completed'
              ? 'bg-emerald-500 border-emerald-500'
              : 'border-border hover:border-amber-500'
          }`}
        >
          {action.status === 'completed' && <Check size={14} className="text-white" weight="bold" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h5 className={`font-medium ${action.status === 'completed' ? 'text-text-tertiary line-through' : 'text-text-primary'}`}>
                {action.title}
              </h5>
              {action.description && (
                <p className="text-sm text-text-secondary mt-1">{action.description}</p>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <span 
                className="px-2 py-0.5 text-xs rounded-full"
                style={{ backgroundColor: `${priorityColors[action.priority]}15`, color: priorityColors[action.priority] }}
              >
                {action.priority}
              </span>
              <span 
                className="px-2 py-0.5 text-xs rounded-full"
                style={{ backgroundColor: `${statusColors[action.status]}15`, color: statusColors[action.status] }}
              >
                {action.status?.replace('_', ' ')}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-4 mt-2 text-xs text-text-tertiary">
            {action.assigned_to_name && (
              <span className="flex items-center gap-1">
                <Users size={12} />
                {action.assigned_to_name}
              </span>
            )}
            {action.due_date && (
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {new Date(action.due_date).toLocaleDateString()}
              </span>
            )}
            {action.department && (
              <span className="flex items-center gap-1">
                <Buildings size={12} />
                {action.department}
              </span>
            )}
          </div>
          
          {/* Progress Bar */}
          {action.status === 'in_progress' && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-surface rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${action.progress_percentage || 0}%` }}
                />
              </div>
              <span className="text-xs text-text-tertiary">{action.progress_percentage || 0}%</span>
            </div>
          )}
        </div>
        
        <button
          onClick={onDelete}
          className="p-2 hover:bg-surface rounded-lg transition-all text-text-tertiary hover:text-red-400"
        >
          <Trash size={16} />
        </button>
      </div>
    </div>
  );
};

// Create Initiative Modal
const CreateInitiativeModal = ({ isOpen, onClose, onCreate, referenceData }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    change_type: 'amendment',
    source_regulator: '',
    effective_date: '',
    compliance_deadline: '',
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Please provide a title');
      return;
    }
    
    setCreating(true);
    try {
      await onCreate(formData);
      setFormData({
        title: '',
        description: '',
        change_type: 'amendment',
        source_regulator: '',
        effective_date: '',
        compliance_deadline: '',
      });
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-surface rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl border border-border"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-border bg-amber-500/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Lightning size={22} className="text-amber-400" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-text-primary">New Change Initiative</h2>
                  <p className="text-xs text-text-secondary">Track a regulatory change</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-background rounded-lg">
                <X size={20} className="text-text-tertiary" />
              </button>
            </div>
          </div>

          <div className="p-5 space-y-4 max-h-[50vh] overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Title <span className="text-red-400">*</span>
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., FICA Amendment Act 2026"
                data-testid="initiative-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the regulatory change..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Change Type</label>
                <Select
                  value={formData.change_type}
                  onValueChange={(value) => setFormData({ ...formData, change_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {referenceData?.change_types?.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    )) || (
                      <>
                        <SelectItem value="new_legislation">New Legislation</SelectItem>
                        <SelectItem value="amendment">Amendment</SelectItem>
                        <SelectItem value="draft_bill">Draft Bill</SelectItem>
                        <SelectItem value="guidance_note">Guidance Note</SelectItem>
                        <SelectItem value="directive">Directive</SelectItem>
                        <SelectItem value="circular">Circular</SelectItem>
                        <SelectItem value="internal_policy">Internal Policy</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Source Regulator</label>
                <Input
                  value={formData.source_regulator}
                  onChange={(e) => setFormData({ ...formData, source_regulator: e.target.value })}
                  placeholder="e.g., SARB, FSCA"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Effective Date</label>
                <Input
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Compliance Deadline</label>
                <Input
                  type="date"
                  value={formData.compliance_deadline}
                  onChange={(e) => setFormData({ ...formData, compliance_deadline: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="p-5 border-t border-border flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={creating || !formData.title.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="create-initiative-submit"
            >
              {creating ? <Spinner size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
              Create Initiative
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Add Impact Modal
const AddImpactModal = ({ isOpen, onClose, onAdd, referenceData }) => {
  const [formData, setFormData] = useState({
    area: 'processes',
    severity: 'medium',
    description: '',
    current_state: '',
    required_state: '',
    regulatory_reference: '',
  });

  const handleSubmit = () => {
    if (!formData.description.trim()) {
      toast.error('Please provide a description');
      return;
    }
    onAdd(formData);
    setFormData({
      area: 'processes',
      severity: 'medium',
      description: '',
      current_state: '',
      required_state: '',
      regulatory_reference: '',
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-surface rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl border border-border"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold text-text-primary">Add Impact Item</h2>
              <button onClick={onClose} className="p-2 hover:bg-background rounded-lg">
                <X size={20} className="text-text-tertiary" />
              </button>
            </div>
          </div>

          <div className="p-5 space-y-4 max-h-[50vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Impact Area</label>
                <Select
                  value={formData.area}
                  onValueChange={(value) => setFormData({ ...formData, area: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(IMPACT_AREAS).map(([value, config]) => (
                      <SelectItem key={value} value={value}>
                        <span className="flex items-center gap-2">
                          <config.icon size={14} style={{ color: config.color }} />
                          {config.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Severity</label>
                <Select
                  value={formData.severity}
                  onValueChange={(value) => setFormData({ ...formData, severity: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SEVERITY_CONFIG).map(([value, config]) => (
                      <SelectItem key={value} value={value}>
                        <span style={{ color: config.color }}>{config.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Description <span className="text-red-400">*</span>
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the impact..."
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Current State</label>
              <Textarea
                value={formData.current_state}
                onChange={(e) => setFormData({ ...formData, current_state: e.target.value })}
                placeholder="What is the current situation?"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Required State</label>
              <Textarea
                value={formData.required_state}
                onChange={(e) => setFormData({ ...formData, required_state: e.target.value })}
                placeholder="What needs to change?"
                rows={2}
              />
            </div>
          </div>

          <div className="p-5 border-t border-border flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-amber-500 hover:bg-amber-600 text-white">
              <Plus size={16} className="mr-2" />
              Add Impact
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Add Action Modal
const AddActionModal = ({ isOpen, onClose, onAdd, referenceData }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    impact_area: 'processes',
    priority: 'medium',
    assigned_to_name: '',
    assigned_to_email: '',
    department: '',
    due_date: '',
    evidence_required: false,
    evidence_description: '',
  });

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      toast.error('Please provide a title');
      return;
    }
    onAdd(formData);
    setFormData({
      title: '',
      description: '',
      impact_area: 'processes',
      priority: 'medium',
      assigned_to_name: '',
      assigned_to_email: '',
      department: '',
      due_date: '',
      evidence_required: false,
      evidence_description: '',
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-surface rounded-xl w-full max-w-lg max-h-[85vh] overflow-hidden shadow-2xl border border-border"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold text-text-primary">Add Action Item</h2>
              <button onClick={onClose} className="p-2 hover:bg-background rounded-lg">
                <X size={20} className="text-text-tertiary" />
              </button>
            </div>
          </div>

          <div className="p-5 space-y-4 max-h-[55vh] overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Title <span className="text-red-400">*</span>
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Action item title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed description of the action..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Impact Area</label>
                <Select
                  value={formData.impact_area}
                  onValueChange={(value) => setFormData({ ...formData, impact_area: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(IMPACT_AREAS).map(([value, config]) => (
                      <SelectItem key={value} value={value}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Priority</label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Assigned To</label>
                <Input
                  value={formData.assigned_to_name}
                  onChange={(e) => setFormData({ ...formData, assigned_to_name: e.target.value })}
                  placeholder="Person's name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Department</label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="e.g., Compliance"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Due Date</label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </div>
          </div>

          <div className="p-5 border-t border-border flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-amber-500 hover:bg-amber-600 text-white">
              <Plus size={16} className="mr-2" />
              Add Action
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ChangeManagementWorkspace;
